/* eslint-disable no-redeclare */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable default-param-last */
import {
  cleanInputCreate,
  cleanInputUpdate,
  createSetting as createMutation,
  type CreateSettingInput,
  type CreateSettingMutationVariables,
  type DateTime,
  deleteSetting as deleteMutation,
  type DeleteSettingMutationVariables,
  getDateTimeDifference,
  getSetting as getQuery,
  getReadId,
  getTableClientId,
  type Setting,
  SETTING_TABLE_NAME,
  syncSettings,
  todayDateTime,
  updateSetting as updateMutation,
  type UpdateSettingInput,
  type SettingType,
} from '@rentguru/commons-utils';
import {
  deleteEntityWithFetchBefore,
  list,
  mutation,
  mutationWithFetchBefore,
  NUMBER_OF_MINUTES_FOR_REFETCH,
} from '@up2rent/graphql';
import isEmpty from 'lodash/isEmpty';
import React, { type Reducer, useContext, useEffect, useMemo, useReducer } from 'react';

import { useUser } from './UserContext';
import { usePermissions } from './utils/PermissionsContext';

export interface SettingContext extends State {
  createSetting: (input: Omit<CreateSettingInput, 'clientId' | 'readId'>) => Promise<Setting>;
  updateSetting: (original: Setting, updates: Partial<UpdateSettingInput>) => Promise<Setting>;
  deleteSetting: (setting: Setting) => Promise<Setting | null>;
  updateOrCreateSetting: (settingType: SettingType, value: string) => Promise<void>;
}

interface State {
  loading: boolean;
  shouldFetch: boolean;
  lastSettingsFetch: DateTime | null;
  settings: Setting[];
}

type Action =
  | {
      type: 'FETCH_SETTINGS';
    }
  | {
      type: 'FETCHED';
      payload: { settings: Setting[] };
    }
  | {
      type: 'FETCH_FAILED';
    }
  | {
      type: 'ADD' | 'UPDATE';
      payload: { setting: Setting };
    }
  | {
      type: 'DELETE';
      payload: { id: string };
    };

const initialState: State = {
  loading: false,
  shouldFetch: true,
  settings: [],
  lastSettingsFetch: null,
};

const contextReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_SETTINGS':
      // Check lastFetch
      if (
        state.loading ||
        state.shouldFetch ||
        (state.lastSettingsFetch &&
          getDateTimeDifference({ start: state.lastSettingsFetch, end: todayDateTime() }, 'minutes') <
            NUMBER_OF_MINUTES_FOR_REFETCH)
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetch: true,
        loading: true,
      };
    case 'FETCHED':
      // Build List with merged object
      const newSettings = [
        ...action.payload.settings,
        ...(state.settings ?? []).filter(
          (oldSetting) => !action.payload.settings.some((newSetting) => newSetting.id === oldSetting.id)
        ),
      ];

      return {
        ...state,
        loading: !isEmpty(state.shouldFetch),
        shouldFetch: false,
        lastSettingsFetch: todayDateTime(),
        settings: newSettings,
      };
    case 'FETCH_FAILED':
      return { ...state, loading: false, shouldFetch: false };
    case 'ADD':
      if (!state.settings) {
        return state;
      }
      const entity = action.payload.setting;
      return {
        ...state,
        settings: [...state.settings, entity],
      };
    case 'UPDATE':
      if (!state.settings) {
        return state;
      }

      return {
        ...state,
        settings: state.settings.map((item) => {
          if (item.id === action.payload.setting.id) {
            return action.payload.setting;
          }
          return item;
        }),
      };
    case 'DELETE':
      if (!state.settings) {
        return state;
      }

      return {
        ...state,
        settings: state.settings.filter((item) => item.id !== action.payload.id),
      };
    default:
      return state;
  }
};

export const SettingContext = React.createContext<SettingContext | null>(null);

export const SettingContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { settingsAutomationWrite } = usePermissions();
  const { clientId } = useUser();
  const [state, dispatch] = useReducer<Reducer<State, Action>>(contextReducer, initialState);

  useEffect(() => {
    let unmounted = false;
    const fetchAndSet = async () => {
      try {
        const settings = await fetchSettings();
        if (!unmounted) {
          dispatch({
            type: 'FETCHED',
            payload: { settings },
          });
        }
      } catch (error) {
        console.error('[settings] fetching settings failed', error);
        if (!unmounted) {
          dispatch({ type: 'FETCH_FAILED' });
        }
      }
    };

    if (state.shouldFetch) {
      fetchAndSet();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetch]);

  const fetchSettings = async (): Promise<Setting[]> =>
    await list<Setting>(syncSettings, 'clientId', getTableClientId(clientId!, SETTING_TABLE_NAME));

  const createSetting = async (
    input: CreateSettingInput | Omit<CreateSettingInput, 'clientId' | 'readId'>
  ): Promise<Setting> => {
    const setting = await mutation<Setting, CreateSettingMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateSettingInput),
        clientId: getTableClientId(clientId!, SETTING_TABLE_NAME),
        readId: getReadId(clientId!, SETTING_TABLE_NAME),
      },
    });
    dispatch({ type: 'ADD', payload: { setting } });
    return setting;
  };

  const updateSetting = async (original: Setting, updates: Partial<UpdateSettingInput>): Promise<Setting> => {
    const setting = await mutationWithFetchBefore<Setting>(original.id, getQuery, updateMutation, {
      input: { ...cleanInputUpdate({ id: original.id, _version: original._version, ...updates }, false) },
    });
    dispatch({ type: 'UPDATE', payload: { setting } });
    return setting;
  };

  const deleteSetting = async (setting: Setting): Promise<Setting | null> => {
    if (!settingsAutomationWrite) {
      return null;
    }
    const result = await deleteEntityWithFetchBefore<Setting, DeleteSettingMutationVariables>(
      setting,
      getQuery,
      deleteMutation
    );
    dispatch({ type: 'DELETE', payload: { id: setting.id } });
    return result;
  };

  const updateOrCreateSetting = async (settingType: SettingType, value: string): Promise<void> => {
    const oldSetting = state.settings.find((setting) => setting.name === settingType);
    if (oldSetting) {
      await updateSetting(oldSetting, { value });
    } else {
      await createSetting({
        name: settingType,
        value,
      });
    }
  };

  const values = useMemo(
    () => ({
      ...state,
      createSetting,
      updateSetting,
      deleteSetting,
      updateOrCreateSetting,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <SettingContext.Provider value={values}>{children}</SettingContext.Provider>;
};

export const useSettings = (): SettingContext => {
  const context = useContext<SettingContext | null>(SettingContext);

  if (!context) {
    throw new Error('`useSettings` hook must be used within a `SettingContextProvider` component');
  }
  return context;
};
